import * as React from 'react';
import { BaSeI18nContext } from '../../contexts/i18n';
import {
  ThemeNamedColorOrColorValue,
  ThemeNamedType,
} from '../../theme/theme-interface';
import { BaSeIcon } from '../image/icon';
import {
  Badge,
  BadgeContainer,
  BadgeContent,
  defaultBorderRadius,
  initialPosition,
  paddingWithIcon,
  paddingWithValue,
  paddingWithoutAnything,
} from './badge-style';
import { mapBadgeValues } from './map-badge-style';

export interface PositionProps {
  right?: string | number;
  top?: string | number;
  left?: string | number;
  bottom?: string | number;
}

export interface IconProps {
  name: string;
  description: string;
  size?: string | number;
}

export interface BadgeProps {
  children?: React.ReactNode;
  color?: ThemeNamedColorOrColorValue;
  value?: string | number | IconProps;
  position?: PositionProps;
  type?: ThemeNamedType;
  show?: boolean;
  borderRadius?: number | string;
  width?: number | string;
  height?: number | string;
  valueNumberLimit?: number;
}

const valueNumberLimitDefault = 9;

export const BaSeBadge: React.FC<BadgeProps> = (props) => {
  const { getMessage } = React.useContext(BaSeI18nContext);

  const {
    color: textColor,
    style: { bgC: backgroundColor, border },
  } = React.useMemo(
    () =>
      mapBadgeValues((props.color as string) ?? 'default')[
        props.type ?? 'primary'
      ],
    [props.color, props.type],
  );

  const showBadge = React.useMemo(
    () => (props.show ?? true) && props.value !== 0,
    [props.show, props.value],
  );

  const padding = React.useMemo(
    () =>
      !props.value
        ? paddingWithoutAnything
        : typeof props.value === 'object'
          ? paddingWithIcon
          : paddingWithValue,
    [props.value],
  );

  const renderValue = React.useCallback(() => {
    const value = props.value;
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number') {
      const limit = props.valueNumberLimit ?? valueNumberLimitDefault;
      if (value > limit) {
        return `${limit}+`;
      }
      if (value < -limit) {
        return `-${limit}`;
      }
      return '' + value;
    }

    return '';
  }, [props.value, props.valueNumberLimit]);

  return (
    <BadgeContainer>
      {props.children}

      {showBadge && (
        <Badge
          right={props.position?.right ?? initialPosition}
          left={props.position?.left ?? initialPosition}
          bottom={props.position?.bottom ?? initialPosition}
          top={props.position?.top ?? initialPosition}
          backgroundColor={backgroundColor}
          border={border}
          color={textColor}
          padding={padding}
          borderRadius={props.borderRadius ?? defaultBorderRadius}
          width={props.width}
          height={props.height}
        >
          <BadgeContent>
            {typeof props.value === 'object' ? (
              <BaSeIcon
                color={textColor}
                name={props.value.name}
                size={props.value.size ?? 16}
                description={
                  props.value.description ??
                  getMessage('icon.label', getMessage('badge.marker'))
                }
              />
            ) : (
              renderValue()
            )}
          </BadgeContent>
        </Badge>
      )}
    </BadgeContainer>
  );
};

BaSeBadge.displayName = 'BaSeBadge';
